/**
 * Меню «Ещё» панели действий дела (доработка шага 11): редкие действия — продление срока, эскалация, переоткрытие,
 * смена отделения — убраны с панели, частые остаются на виду. Набор действий и права не меняются: что в меню, решает
 * тот же блок `permissions`, что и для кнопок.
 *
 * Открывается кнопкой; закрывается повторным нажатием, щелчком мимо, выбором действия и Esc (Esc не закрывает дело:
 * событие дальше не идёт, фокус возвращается на кнопку «Ещё»).
 */
import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

export function MoreMenu({ children }: { children: ReactNode }) {
    const [open, setOpen] = useState(false);
    const id = useId();
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (event: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape' && open) {
            event.stopPropagation();
            setOpen(false);
            buttonRef.current?.focus();
        }
    };

    return (
        <div className="more-menu" ref={wrapRef} onKeyDown={onKeyDown}>
            <button
                ref={buttonRef}
                type="button"
                className="act-btn"
                aria-expanded={open}
                aria-controls={id}
                onClick={() => setOpen((value) => !value)}
            >
                Ещё <span aria-hidden="true">▾</span>
            </button>
            {open ? (
                <div
                    className="more-panel"
                    id={id}
                    role="group"
                    aria-label="Другие действия по делу"
                    onClick={(event) => {
                        // Выбор действия закрывает меню; подсказка «?» и раскрытие списка отделений — нет.
                        if ((event.target as HTMLElement).closest('button.act-btn')) setOpen(false);
                    }}
                    onChange={() => setOpen(false)}
                >
                    {children}
                </div>
            ) : null}
        </div>
    );
}

export default MoreMenu;
